import { type Locale, parseISO, format, formatDistance, formatDistanceToNow, isThisYear, compareAsc, compareDesc } from 'date-fns'
import { TZDateMini as TZDate } from '@date-fns/tz'
import { UTCDateMini as UTCDate } from '@date-fns/utc'
import { enGB } from 'date-fns/locale'
import i18n from 'i18n'

// NOTE: sw locale is missing in date-fns so it will default to enGB
const dateLocaleImporters = new Map<string, () => Promise<Record<string, Locale>>>([
  ['af', () => import('date-fns/locale/af')],
  ['ar', () => import('date-fns/locale/ar')],
  ['bg', () => import('date-fns/locale/bg')],
  ['cs', () => import('date-fns/locale/cs')],
  ['de', () => import('date-fns/locale/de')],
  ['el', () => import('date-fns/locale/el')],
  ['en-us', () => import('date-fns/locale/en-US')],
  ['et', () => import('date-fns/locale/et')],
  ['fil', () => import('date-fns/locale/fi')],
  ['fr', () => import('date-fns/locale/fr')],
  ['hr', () => import('date-fns/locale/hr')],
  ['hu', () => import('date-fns/locale/hu')],
  ['id', () => import('date-fns/locale/id')],
  ['lt', () => import('date-fns/locale/lt')],
  ['mk', () => import('date-fns/locale/mk')],
  ['ms', () => import('date-fns/locale/ms')],
  ['nn', () => import('date-fns/locale/nn')],
  ['pl', () => import('date-fns/locale/pl')],
  ['pt', () => import('date-fns/locale/pt')],
  ['ro', () => import('date-fns/locale/ro')],
  ['ru', () => import('date-fns/locale/ru')],
  ['sk', () => import('date-fns/locale/sk')],
  ['sl', () => import('date-fns/locale/sl')],
  ['sr', () => import('date-fns/locale/sr')],
  ['th', () => import('date-fns/locale/th')],
  ['tr', () => import('date-fns/locale/tr')],
  ['uk', () => import('date-fns/locale/uk')],
  ['vi', () => import('date-fns/locale/vi')],
  ['zh', () => import('date-fns/locale/zh-CN')],
  ['zh-tw', () => import('date-fns/locale/zh-TW')],
])

// NOTE: cache of already-loaded locales, keyed by locale code
const dateLocaleCache = new Map<string, Locale>([['en', enGB]])
// NOTE: last successfully resolved locale, used as a fallback while a newly requested locale is still loading
let lastLoadedLocale: Locale = enGB

const loadDateLocale = async (locale: string): Promise<void> => {
  if (dateLocaleCache.has(locale)) {
    return
  }
  const loader = dateLocaleImporters.get(locale)
  if (!loader) {
    return
  }
  try {
    const module = await loader()
    // NOTE: each date-fns locale module has a single named export (not default) whose name varies per locale
    // (e.g. `af`, `enUS`, `zhCN`), so we pluck whatever the sole exported value is instead of hardcoding each name
    const loadedLocale = Object.values(module)[0] ?? enGB
    dateLocaleCache.set(locale, loadedLocale)
    lastLoadedLocale = loadedLocale
  } catch {
    // NOTE: keep using the fallback locale if the dynamic import fails (e.g. offline)
  }
}

// NOTE: preload the locale as soon as the language changes, so the fallback window is as short as possible
i18n.on('languageChanged', (locale: string) => {
  void loadDateLocale(locale ?? 'en')
})

export const getDateLocale = (): Locale => {
  const cached = dateLocaleCache.get(i18n.language)
  if (cached) {
    return cached
  }
  void loadDateLocale(i18n.language)
  return lastLoadedLocale
}

export const getDateFormat = (format: string): string => {
  // NOTE: missing from date-fns, based on existing localizations
  let shortNoYear: string
  let mediumNoYear: string
  switch (i18n.language) {
    case 'af':
      shortNoYear = 'MM/dd'
      mediumNoYear = 'MMM yyyy'
      break
    case 'mk':
    case 'bg':
      shortNoYear = 'dd/MM'
      mediumNoYear = 'dd MMM'
      break
    case 'cs':
      shortNoYear = 'dd.MM'
      mediumNoYear = 'd. M.'
      break
    case 'pl':
    case 'de':
      shortNoYear = 'dd.MM'
      mediumNoYear = 'do MMM'
      break
    case 'el':
      shortNoYear = 'd/M'
      mediumNoYear = 'd MMM'
      break
    case 'en-us':
      shortNoYear = 'MM/dd'
      mediumNoYear = 'MMM d'
      break
    case 'nn':
    case 'et':
      shortNoYear = 'dd.MM'
      mediumNoYear = 'd. MMM'
      break
    case 'hr':
      shortNoYear = 'dd. MM.'
      mediumNoYear = 'd. MMM'
      break
    case 'hu':
      shortNoYear = 'MM. dd.'
      mediumNoYear = 'MMM d.'
      break
    case 'ms':
    case 'id':
      shortNoYear = 'd/M'
      mediumNoYear = 'd MMM'
      break
    case 'lt':
      shortNoYear = 'MM-dd'
      mediumNoYear = 'MM-dd'
      break
    case 'pt':
      shortNoYear = 'dd/MM'
      mediumNoYear = 'd \'de\' MMM'
      break
    case 'ro':
      shortNoYear = 'dd.MM'
      mediumNoYear = 'd MMMM'
      break
    case 'ru':
      shortNoYear = 'dd.MM'
      mediumNoYear = 'd MMM'
      break
    case 'sk':
      shortNoYear = 'd. M.'
      mediumNoYear = 'd. M.'
      break
    case 'sl':
    case 'sr':
      shortNoYear = 'd. MM.'
      mediumNoYear = 'd. MMM'
      break
    case 'th':
      shortNoYear = 'dd/MM'
      mediumNoYear = 'd MMM'
      break
    case 'tr':
    case 'uk':
      shortNoYear = 'dd.MM.'
      mediumNoYear = 'd MMM'
      break
    case 'zh':
    case 'zh-tw':
      shortNoYear = 'MM-dd'
      mediumNoYear = 'MM-dd'
      break
    case 'fr':
    case 'ar':
    case 'vi':
    case 'fil':
    case 'sw':
    case 'en':
    default:
      shortNoYear = 'dd/MM'
      mediumNoYear = 'd MMM'
      break
  }

  // NOTE: returns localized variants
  switch (format) {
    case 'iso_no_tz':
      return 'yyyy-MM-dd\'T\'HH:mm:ss' // 2022-04-29T12:00:00
    case 'full':
      return 'PP p' // Apr 29, 2022 12:00 AM
    case 'full_no_year':
      return `${mediumNoYear} p` // Apr 29 12:00 AM
    case 'medium':
      return 'PP' // Apr 29, 2022
    case 'medium_no_year':
      return mediumNoYear
    case 'short':
      return 'P' // 04/29/2022
    case 'short_with_time':
      return 'Pp' // 04/29/2022, 12:00 AM
    case 'short_no_year':
      return shortNoYear
    case 'short_no_year_with_time':
      return `${shortNoYear} p`
    case 'time':
      return 'p' // 12:00 AM
    case 'gmt':
      return 'O' // GMT-8, GMT+5:30, GMT+0
    case 'offset':
      return 'XXX' // -08:00, +05:30, Z
    case 'iso':
    default:
      return 'yyyy-MM-dd\'T\'HH:mm:ssXXX' // 2022-04-29T12:00:00+02:00
  }
}

type dateValue = Date | string | null

const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/i // NOSONAR

// NOTE: extracts the UTC offset embedded in an ISO datetime string (e.g. "Z" or "+02:00"),
// normalizing "Z" to "UTC" since it isn't a valid IANA timezone/offset name on its own.
// Returns null when the string isn't a recognizable ISO datetime.
export const resolveIsoTimeZone = (isoValue: string): string | null => {
  const parts = isoDateRegex.exec(isoValue)
  if (!parts) {
    return null
  }
  return parts[7] && parts[7] !== 'Z' ? parts[7] : 'UTC'
}

export const zonedDate = (value: dateValue, dateFormat = 'medium', timeZone?: string): string | null => {
  if (!value) {
    return null
  }
  if (typeof value === 'string') {
    const timeZoneFromValue = resolveIsoTimeZone(value)
    if (!timeZoneFromValue) {
      return null
    }
    timeZone ??= timeZoneFromValue
  }
  let zonedValue = value
  if (timeZone === 'UTC' || timeZone === 'Z') {
    if (typeof value === 'string') {
      zonedValue = new UTCDate(value)
    } else {
      zonedValue = new UTCDate(value.getTime())
    }
  } else if (timeZone) {
    if (typeof value === 'string') {
      zonedValue = new TZDate(value, timeZone)
    } else {
      zonedValue = new TZDate(value.getTime(), timeZone)
    }
  }
  return format(zonedValue, getDateFormat(dateFormat), { locale: getDateLocale() })
}

export const compactZonedDate = (value: dateValue, timeZone?: string): string | null => {
  if (!value) {
    return null
  }
  const date = typeof value === 'string' ? parseISO(value) : value
  const fullDateFormat = isThisYear(date) ? 'full_no_year' : 'full'
  return zonedDate(date, fullDateFormat, timeZone)
}

// NOTE: always renders in the viewer's own browser timezone, ignoring any offset embedded in
// the ISO string - this is the correct default for most displayed dates (see note on `zonedDate`)
export const localDate = (value: dateValue, dateFormat = 'medium'): string | null => {
  if (!value) {
    return null
  }
  const date = typeof value === 'string' ? parseISO(value) : value
  return format(date, getDateFormat(dateFormat), { locale: getDateLocale() })
}

export const compactLocalDate = (value: dateValue): string | null => {
  if (!value) {
    return null
  }
  const date = typeof value === 'string' ? parseISO(value) : value
  const fullDateFormat = isThisYear(date) ? 'full_no_year' : 'full'
  return localDate(date, fullDateFormat)
}

export const relativeDate = (first: dateValue, second?: dateValue): string | null => {
  if (!first) {
    return null
  }
  const firstDate = typeof first === 'string' ? parseISO(first) : first
  if (!second) {
    return formatDistanceToNow(firstDate, { locale: getDateLocale(), addSuffix: true })
  }
  const secondDate = typeof second === 'string' ? parseISO(second) : second
  return formatDistance(firstDate, secondDate, { locale: getDateLocale(), addSuffix: false })
}

export const compareDatesAsc = (a: string, b: string): number => {
  const firstDate = parseISO(a)
  const secondDate = parseISO(b)
  return compareAsc(firstDate, secondDate)
}

export const compareDatesDesc = (a: string, b: string): number => {
  const firstDate = parseISO(a)
  const secondDate = parseISO(b)
  return compareDesc(firstDate, secondDate)
}
